/**
 * Cart Sidebar Component (S2)
 * Backend-backed shopping cart with real-time inventory validation
 */
import React, { Fragment, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ShoppingBagIcon,
  TrashIcon,
  MinusIcon,
  PlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { CartItem as CartItemType } from '../types';

// Memoized cart item component to prevent re-renders when other items change
const CartItemRow = React.memo(function CartItemRow({
  item,
  isLoading,
  onUpdateQuantity,
  onRemove
}: {
  item: CartItemType;
  isLoading: boolean;
  onUpdateQuantity: (itemId: number, newQuantity: number) => void;
  onRemove: (itemId: number) => void;
}) {
  return (
    <div className="flex gap-4">
      {/* Image */}
      <div className="h-24 w-24 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
        {item.primary_image ? (
          <img
            src={item.primary_image}
            alt={item.product_title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <ShoppingBagIcon className="h-8 w-8 text-gray-300" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {item.product_title}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{item.variant_title}</p>
            {item.variant_size && (
              <p className="text-xs text-gray-500 mt-1">Size: {item.variant_size}</p>
            )}

            {/* Availability Warning */}
            {!item.is_available && (
              <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>Only {item.available_to_sell} available</span>
              </div>
            )}
          </div>
          <button
            onClick={() => onRemove(item.id)}
            className="ml-2 text-gray-400 hover:text-red-500 transition-colors"
            disabled={isLoading}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Quantity and Price */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              className="p-1 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              <MinusIcon className="h-4 w-4 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-900 w-8 text-center">
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              className="p-1 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isLoading || item.quantity >= item.available_to_sell}
            >
              <PlusIcon className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          <p className="text-sm font-medium text-gray-900">
            ${Number(item.line_total).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
});

export default function CartSidebar() {
  const {
    cart,
    isCartOpen,
    closeCart,
    removeCartItem,
    updateCartItem,
    isLoading,
    error
  } = useCart();

  const navigate = useNavigate();

  const handleContinueToCheckout = useCallback(() => {
    closeCart();
    navigate('/checkout');
  }, [closeCart, navigate]);

  const handleUpdateQuantity = useCallback((itemId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeCartItem(itemId);
    } else {
      updateCartItem(itemId, newQuantity);
    }
  }, [removeCartItem, updateCartItem]);

  const handleRemoveItem = useCallback((itemId: number) => {
    removeCartItem(itemId);
  }, [removeCartItem]);

  const cartItems = cart?.items || [];
  const itemCount = cart?.item_count || 0;
  const subtotal = Number(cart?.subtotal || 0);
  const hasUnavailableItems = cart?.has_unavailable_items || false;

  return (
    <Transition.Root show={isCartOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeCart}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-full max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Shopping Cart ({itemCount})
                      </Dialog.Title>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                        onClick={closeCart}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    )}

                    {/* Loading State */}
                    {isLoading && cartItems.length === 0 && (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    )}

                    {/* Cart Items */}
                    {!isLoading && (
                      <div className="flex-1 overflow-y-auto px-6 py-6">
                        {cartItems.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full text-center">
                            <ShoppingBagIcon className="h-16 w-16 text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
                            <p className="text-sm text-gray-500 mb-6">Add some items to get started</p>
                            <button
                              onClick={() => {
                                closeCart();
                                navigate('/shop');
                              }}
                              className="btn btn-primary"
                            >
                              Continue Shopping
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {cartItems.map((item) => (
                              <CartItemRow
                                key={item.id}
                                item={item}
                                isLoading={isLoading}
                                onUpdateQuantity={handleUpdateQuantity}
                                onRemove={handleRemoveItem}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    {cartItems.length > 0 && (
                      <div className="border-t border-gray-200 px-6 py-6 space-y-4">
                        {/* Availability Warning */}
                        {hasUnavailableItems && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800">
                              Some items in your cart have limited availability. Please review quantities before checkout.
                            </p>
                          </div>
                        )}

                        {/* Subtotal */}
                        <div className="flex items-center justify-between text-base font-medium text-gray-900">
                          <p>Subtotal</p>
                          <p>${subtotal.toFixed(2)}</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          Delivery and taxes calculated at checkout.
                        </p>

                        {/* Actions */}
                        <button
                          onClick={handleContinueToCheckout}
                          className="w-full btn btn-primary py-3 text-base font-medium"
                          disabled={hasUnavailableItems}
                        >
                          Continue to Checkout
                        </button>
                        <button
                          onClick={closeCart}
                          className="w-full btn btn-secondary py-3 text-base font-medium"
                        >
                          Continue Shopping
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
